import { useState } from 'react';
import { api } from '@/lib/api';

const COOPERATION_TYPES = ['人才培訓合作', '課程聯合開發', '品牌贊助', '校園招募合作', '企業客製課', '其他'];

const PartnerForm = () => {
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [formData, setFormData] = useState({
    company: '',
    name: '',
    email: '',
    phone: '',
    type: '',
    size: '',
    message: '',
  });

  const handleChange = (field: string, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!formData.company || !formData.name || !formData.email || !formData.type) return;
    setSubmitting(true);
    setSubmitError('');
    try {
      await api.submitWebsiteContact({
        formType: 1,
        companyName: formData.company,
        contactName: formData.name,
        email: formData.email,
        phone: formData.phone,
        cooperationType: formData.type,
        companySize: formData.size,
        message: formData.message
      });
      setSubmitted(true);
    } catch (error) {
      setSubmitError(error instanceof Error ? error.message : '提交失敗，請稍後重試');
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-center">
        <div className="w-16 h-16 flex items-center justify-center bg-emerald-100 rounded-full mb-5">
          <i className="ri-check-line text-emerald-600 text-2xl" />
        </div>
        <h3 className="text-xl font-bold text-[#0A1628] mb-2">提交成功！</h3>
        <p className="text-slate-500 text-sm leading-relaxed max-w-xs">
          感謝你的合作意向，我們的商務團隊將在 <strong>1-2 個工作日</strong>內與你取得聯繫。
        </p>
        <button
          onClick={() => { setSubmitted(false); setFormData({ company: '', name: '', email: '', phone: '', type: '', size: '', message: '' }); }}
          className="mt-6 px-5 py-2.5 bg-violet-600 text-white text-sm font-semibold rounded-xl cursor-pointer hover:bg-violet-700 transition-colors whitespace-nowrap"
        >
          再次提交
        </button>
      </div>
    );
  }

  return (
    <form
      data-readdy-form
      onSubmit={handleSubmit}
      className="space-y-5"
    >
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-xs font-semibold text-slate-600 mb-1.5">
            公司/機構名稱 <span className="text-rose-500">*</span>
          </label>
          <input
            name="company"
            type="text"
            required
            value={formData.company}
            onChange={(e) => handleChange('company', e.target.value)}
            placeholder="例：NexusGo Inc."
            className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm text-slate-700 placeholder-slate-300 focus:outline-none focus:border-violet-400 focus:bg-white transition-all"
          />
        </div>
        <div>
          <label className="block text-xs font-semibold text-slate-600 mb-1.5">
            聯絡人姓名 <span className="text-rose-500">*</span>
          </label>
          <input
            name="name"
            type="text"
            required
            value={formData.name}
            onChange={(e) => handleChange('name', e.target.value)}
            placeholder="你的全名"
            className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm text-slate-700 placeholder-slate-300 focus:outline-none focus:border-violet-400 focus:bg-white transition-all"
          />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-xs font-semibold text-slate-600 mb-1.5">
            工作信箱 <span className="text-rose-500">*</span>
          </label>
          <input
            name="email"
            type="email"
            required
            value={formData.email}
            onChange={(e) => handleChange('email', e.target.value)}
            placeholder="you@company.com"
            className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm text-slate-700 placeholder-slate-300 focus:outline-none focus:border-violet-400 focus:bg-white transition-all"
          />
        </div>
        <div>
          <label className="block text-xs font-semibold text-slate-600 mb-1.5">聯絡電話</label>
          <input
            name="phone"
            type="tel"
            value={formData.phone}
            onChange={(e) => handleChange('phone', e.target.value)}
            placeholder="+886 912 345 678"
            className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm text-slate-700 placeholder-slate-300 focus:outline-none focus:border-violet-400 focus:bg-white transition-all"
          />
        </div>
      </div>

      <div>
        <label className="block text-xs font-semibold text-slate-600 mb-2">
          合作類型 <span className="text-rose-500">*</span>
        </label>
        <div className="flex flex-wrap gap-2">
          {COOPERATION_TYPES.map((type) => (
            <button
              key={type}
              type="button"
              onClick={() => handleChange('type', type)}
              className={`px-3 py-2 rounded-xl text-xs font-semibold cursor-pointer transition-all whitespace-nowrap ${
                formData.type === type
                  ? 'bg-violet-600 text-white'
                  : 'bg-slate-100 text-slate-600 hover:bg-violet-50 hover:text-violet-700'
              }`}
            >
              {type}
            </button>
          ))}
        </div>
        <input type="hidden" name="type" value={formData.type} />
      </div>

      <div>
        <label className="block text-xs font-semibold text-slate-600 mb-1.5">公司規模</label>
        <select
          name="size"
          value={formData.size}
          onChange={(e) => handleChange('size', e.target.value)}
          className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm text-slate-600 focus:outline-none focus:border-violet-400 focus:bg-white transition-all cursor-pointer"
        >
          <option value="">請選擇</option>
          <option value="1-10人">1–10 人（初創團隊）</option>
          <option value="11-50人">11–50 人</option>
          <option value="51-200人">51–200 人</option>
          <option value="201-1000人">201–1000 人</option>
          <option value="1000人以上">1000 人以上（大型企業）</option>
        </select>
      </div>

      <div>
        <label className="block text-xs font-semibold text-slate-600 mb-1.5">合作需求說明</label>
        <textarea
          name="message"
          value={formData.message}
          onChange={(e) => {
            if (e.target.value.length <= 500) handleChange('message', e.target.value);
          }}
          placeholder="請簡要描述你的合作需求、目標和預期時間線..."
          rows={4}
          className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-sm text-slate-700 placeholder-slate-300 focus:outline-none focus:border-violet-400 focus:bg-white transition-all resize-none"
        />
        <p className="text-xs text-slate-400 mt-1 text-right">{formData.message.length}/500</p>
      </div>

      <button
        type="submit"
        disabled={submitting || !formData.company || !formData.name || !formData.email || !formData.type}
        className="w-full py-3.5 bg-violet-600 hover:bg-violet-700 disabled:bg-slate-200 disabled:text-slate-400 text-white font-bold text-sm rounded-xl cursor-pointer transition-all whitespace-nowrap disabled:cursor-not-allowed"
      >
        {submitting ? (
          <span className="flex items-center justify-center gap-2">
            <i className="ri-loader-4-line animate-spin" />
            提交中...
          </span>
        ) : '提交合作申請'}
      </button>
      {submitError ? <p className="text-center text-xs text-rose-500">{submitError}</p> : null}
    </form>
  );
};

export default PartnerForm;
